import {CardOrder, isSameOrder, useCardOrder} from "./useCardOrder";
import {
  Box,
  Col,
  DSButton,
  DSIconButton,
  DSIconGlobe,
  DSIconPyramid,
  DSIconSortAsc,
  DSIconSortDesc,
  DSIconUser,
  DSRawButton,
  Row,
} from "@cdx/ds";
import {useEsc, ToggleButton, TooltipForChild, TooltipOptions} from "@cdx/common";
import useIsMetaKeyDown from "../../lib/hooks/useIsMetaKeyDown";
import {HelperDropDown} from "../../components/Helper";
import {getMetaKeyLabel} from "@cdx/common/device-utils";
import {ordinalSuffix} from "../../lib/utils";
import rawOrderInfo from "./order-info";
import {getLimits} from "../billing/billing-utils";
import {
  hasPermissionToModifyMilestone,
  hasPermissionToModifyPreferredOrderInDeck,
  hasPermissionToModifySprint,
} from "../../lib/permissions";
import {completeOnboardingStep, ONBOARDING_STEPS} from "../onboarding/onboarding-utils";
import {cardContainerStyles} from "../card-container/unified-card-container.css";
import {api} from "../../lib/api";
import {ReactNode} from "react";
import {Root} from "../../cdx-models/Root";
import {ArenaCtx} from "../card-panel/card-panel-utils";
import {Card} from "../../cdx-models/Card";

const orderInfo = rawOrderInfo as any;

const SecondaryPill = ({rank}: {rank: number}) => (
  <TooltipForChild
    tooltip={`Active secondary sort option with ${rank}${ordinalSuffix(rank)} priority`}
  >
    <Col
      align="center"
      justify="center"
      lineHeight="none"
      bold
      rounded="full"
      flex="none"
      width="16px"
      height="16px"
      size={10}
      colorTheme="active600"
      bg="foreground"
      color="primary"
    >
      {rank}
    </Col>
  </TooltipForChild>
);

const DefaultOrderButton = ({
  current,
  disabled,
  updateFn,
}: {
  current: CardOrder;
  disabled: boolean;
  updateFn: (next: CardOrder) => void;
}) => (
  <TooltipForChild
    targetIsDisabled={disabled}
    tooltip="Set current order as default order for all users"
  >
    <DSButton variant="secondary" size="sm" onClick={() => updateFn(current)} disabled={disabled}>
      Save
    </DSButton>
  </TooltipForChild>
);

type ButtonInfo = {
  isMetaKeyDown: boolean;
  current: CardOrder;
  setOrder: (next: CardOrder) => void;
};

const Group = ({
  label,
  children,
  buttonInfo,
  prop,
}: {
  label: string;
  children: ReactNode;
  buttonInfo: ButtonInfo;
  prop: string;
}) => {
  const currSecondary = buttonInfo.current.secondary || [];
  const isSecondaryIndex = currSecondary.findIndex((sec: CardOrder) => sec.prop === prop);
  return (
    <Col relative>
      <Row align="center" sp="4px">
        <Box align="center" relative flex="auto">
          <Box size={12} color="primary">
            {label}
          </Box>
          {isSecondaryIndex >= 0 && currSecondary.length > 0 && (
            <Row absolute right="0" top="0" bottom="0" align="center" pr="4px">
              <SecondaryPill rank={isSecondaryIndex + 1} />
            </Row>
          )}
        </Box>
        <Row sp="4px">{children}</Row>
      </Row>
    </Col>
  );
};

const isSameBaseOrder = (o1: CardOrder, o2: CardOrder) =>
  o1.prop === o2.prop && Boolean(o1.isReversed) === Boolean(o2.isReversed);

const getButtonProps = ({
  buttonInfo,
  targetOrder,
}: {
  buttonInfo: ButtonInfo;
  targetOrder: CardOrder;
}) => {
  const active = isSameBaseOrder(buttonInfo.current, targetOrder);
  const currSecondary = buttonInfo.current.secondary || [];
  const targetOrderInfo = orderInfo[targetOrder.prop];

  const validSecondary = targetOrderInfo && !targetOrderInfo.multiKey && !targetOrderInfo.noCompact;
  const currSecondaryHasTargetPropIdx = validSecondary
    ? currSecondary.findIndex((sec) => sec.prop === targetOrder.prop)
    : -1;
  const isAlreadySecondaryOrder =
    currSecondaryHasTargetPropIdx >= 0 &&
    currSecondary[currSecondaryHasTargetPropIdx].isReversed === targetOrder.isReversed;

  const handleClick = () => {
    completeOnboardingStep(ONBOARDING_STEPS.useSorting);
    buttonInfo.setOrder(targetOrder);
  };

  const handleMetaClick = () => {
    const cleanedUp = currSecondary.filter((c) => c.prop !== targetOrder.prop);
    buttonInfo.setOrder({
      ...buttonInfo.current,
      secondary: isAlreadySecondaryOrder ? cleanedUp : [...cleanedUp, targetOrder],
    });
  };

  const isDisabled = () => {
    if (buttonInfo.isMetaKeyDown) {
      return (
        buttonInfo.current.prop === "manual" ||
        !validSecondary ||
        buttonInfo.current.prop === targetOrder.prop
      );
    }
    return !validSecondary && buttonInfo.current.isCompact;
  };
  return {
    isSecondaryIndex: isAlreadySecondaryOrder ? currSecondaryHasTargetPropIdx : -1,
    props: {
      active,
      autoFocus: active,
      onClick: handleClick,
      onMetaClick: handleMetaClick,
      disabled: isDisabled(),
    },
  };
};

type MiniSortButtonProps = {
  prop: string;
  isReversed?: boolean;
  buttonInfo: ButtonInfo;
  icon: ReactNode;
  tooltip?: string;
  tooltipProps?: TooltipOptions;
};
const MiniSortButton = ({
  prop,
  isReversed,
  buttonInfo,
  icon,
  tooltip,
  tooltipProps,
}: MiniSortButtonProps) => (
  <TooltipForChild tooltip={tooltip} {...tooltipProps}>
    <DSIconButton
      size="sm"
      variant="secondary"
      {...getButtonProps({buttonInfo, targetOrder: {prop, isReversed: Boolean(isReversed)}}).props}
      icon={icon}
    />
  </TooltipForChild>
);

type SortButtonProps = {
  prop: string;
  buttonInfo: ButtonInfo;
  children: string;
  tooltip?: string;
  tooltipProps?: TooltipOptions;
  icon?: ReactNode;
};

const SortButton = ({
  prop,
  buttonInfo,
  children,
  tooltip,
  tooltipProps,
  icon,
  ...rest
}: SortButtonProps) => {
  const {isSecondaryIndex, props} = getButtonProps({
    buttonInfo,
    targetOrder: {prop, isReversed: false},
  });
  return (
    <Col relative>
      <TooltipForChild tooltip={tooltip} {...tooltipProps}>
        {icon ? (
          <DSRawButton variant="secondary" {...props} size="sm" {...rest}>
            <Row sp="4px" align="center" justify="center">
              {icon}
              <span>{children}</span>
            </Row>
          </DSRawButton>
        ) : (
          <DSButton variant="secondary" {...props} size="sm" {...rest}>
            {children}
          </DSButton>
        )}
      </TooltipForChild>
      {isSecondaryIndex >= 0 && (buttonInfo.current.secondary || []).length > 0 && (
        <Row absolute right="0" top="0" bottom="0" align="center" style={{marginRight: -8}}>
          <SecondaryPill rank={isSecondaryIndex + 1} />
        </Row>
      )}
    </Col>
  );
};

const showManualButton = ({arenaCtx, root}: {arenaCtx: ArenaCtx; root: Root}) => {
  if (arenaCtx.type === "deck" || arenaCtx.type === "milestone" || arenaCtx.type === "sprint") {
    if (process.env.REACT_APP_MODE === "open") {
      return root.account.$meta.exists("cardOrders", {
        context: arenaCtx.type,
      });
    }
    return true;
  }
  return false;
};

type PreferredOrder = {
  preferredOrder: CardOrder;
  mayModify: boolean | null;
  updateFn: (next: CardOrder) => void;
};

const getPreferredOrder = (arenaCtx: ArenaCtx, root: Root): PreferredOrder | null => {
  switch (arenaCtx.type) {
    case "deck":
      const deckId = arenaCtx.model.id;
      return {
        preferredOrder: arenaCtx.model.preferredOrder,
        mayModify: hasPermissionToModifyPreferredOrderInDeck({
          root,
          project: arenaCtx.model.project,
        }),
        updateFn: (next) => api.mutate.decks.update({id: deckId, preferredOrder: next}),
      };
    case "milestone":
      const milestoneId = arenaCtx.model.id;
      return {
        preferredOrder: arenaCtx.model.preferredOrder,
        mayModify: hasPermissionToModifyMilestone({root, milestone: arenaCtx.model}),
        updateFn: (next) => api.mutate.milestones.update({id: milestoneId, preferredOrder: next}),
      };
    case "sprint":
      const sprintConfigId = arenaCtx.model.sprintConfig.id;
      return {
        preferredOrder: arenaCtx.model.sprintConfig.preferredOrder,
        mayModify: hasPermissionToModifySprint({root, sprint: arenaCtx.model}),
        updateFn: (next) =>
          api.mutate.sprints.updateConfig({
            id: sprintConfigId,
            preferredOrder: next,
          }),
      };
    default:
      return null;
  }
};

export const getPreferredOrderFromContext = getPreferredOrder;

type SortPanelProps = {
  arenaCtx: ArenaCtx;
  onClose: () => void;
  cards: Card[];
  root: Root;
  onLight: boolean;
};

const SortPanel = ({arenaCtx, onClose, cards, root, onLight}: SortPanelProps) => {
  const [current, {setOrder, setCompact, setHideEmpty, setMode, clear}] = useCardOrder();
  const limits = getLimits(root.account);
  const withDueDate = limits.withDueDates === "true";
  const isMetaKeyDown = useIsMetaKeyDown();
  const withBeasts = limits.withBeastMode === "true";

  useEsc(onClose);

  const handleSetOrder = ({prop, isReversed = false, secondary = null}: CardOrder) => {
    setOrder({prop, isReversed, secondary});
  };

  const getPreferredOrderInfo = () => {
    const info = getPreferredOrder(arenaCtx, root);
    if (!info) return null;
    return {
      isActive: isSameOrder(current, info.preferredOrder),
      preferredOrder: info.preferredOrder,
      hasPermissionToModify: info.mayModify,
      updateFn: info.updateFn,
    };
  };

  const prefOrderInfo = getPreferredOrderInfo();

  const bag = {
    setOrder: handleSetOrder,
    onLight,
    current,
    preferredOrder: prefOrderInfo?.preferredOrder,
    isMetaKeyDown,
  };

  const canSetPrefOrder =
    process.env.REACT_APP_MODE !== "open" && (prefOrderInfo?.hasPermissionToModify ?? false);
  const currOrderInfo = orderInfo[current.prop];

  return (
    <Col
      py="8px"
      sp="8px"
      position="sticky"
      alignSelf="start"
      style={{width: 120}}
      className={cardContainerStyles.stickyTop}
      flex="none"
      data-cdx-context="SortPanel"
    >
      <Row sp="4px" align="center">
        <Box as="h3" bold size={16} color="primary" flex="auto">
          Ordering
        </Box>
        <HelperDropDown maxWidth={250}>
          <p>
            Use this panel to group your cards. You can use <code>{getMetaKeyLabel()} + Click</code>{" "}
            to set a secondary sort order within each group. By default the secondary order is based
            on priority, effort and creation date.
          </p>
          {canSetPrefOrder && (
            <p>
              Setting a <b>Default Order</b> for this deck allows to preset the initial order for
              your team members or visitors in case this is a public deck.
            </p>
          )}
        </HelperDropDown>
      </Row>
      <Col sp="24px">
        <Col sp="16px">
          <Col sp="8px">
            <Group label="Priority" prop="priority" buttonInfo={bag}>
              <MiniSortButton buttonInfo={bag} prop="priority" icon={<DSIconSortDesc />} />
              <MiniSortButton
                buttonInfo={bag}
                prop="priority"
                isReversed
                icon={<DSIconSortAsc />}
              />
            </Group>
            <Group label="Effort" prop="effort" buttonInfo={bag}>
              <MiniSortButton buttonInfo={bag} prop="effort" isReversed icon={<DSIconSortDesc />} />
              <MiniSortButton buttonInfo={bag} prop="effort" icon={<DSIconSortAsc />} />
            </Group>
            <Group label="State" prop="status" buttonInfo={bag}>
              <MiniSortButton
                buttonInfo={bag}
                prop="status"
                isReversed
                tooltip="Sort from done to unassigned"
                icon={<DSIconSortDesc />}
              />
              <MiniSortButton
                buttonInfo={bag}
                prop="status"
                tooltip="Sort from unassigned to done"
                icon={<DSIconSortAsc />}
              />
            </Group>
            {root.account.sprintsEnabled &&
              process.env.REACT_APP_MODE !== "open" &&
              arenaCtx.type !== "sprint" && (
                <Group label="Run" prop="sprint" buttonInfo={bag}>
                  <MiniSortButton
                    buttonInfo={bag}
                    prop="sprint"
                    isReversed
                    icon={<DSIconSortDesc />}
                  />
                  <MiniSortButton buttonInfo={bag} prop="sprint" icon={<DSIconSortAsc />} />
                </Group>
              )}
            {root.account.milestonesEnabled &&
              arenaCtx.type !== "milestone" &&
              process.env.REACT_APP_MODE !== "open" && (
                <Group label="Milestone" prop="milestone" buttonInfo={bag}>
                  <MiniSortButton
                    buttonInfo={bag}
                    prop="milestone"
                    isReversed
                    icon={<DSIconSortDesc />}
                  />
                  <MiniSortButton buttonInfo={bag} prop="milestone" icon={<DSIconSortAsc />} />
                </Group>
              )}
            <Group label="Tags" prop="tags" buttonInfo={bag}>
              <MiniSortButton
                tooltip="Order by project tags"
                buttonInfo={bag}
                prop="tagsProject"
                icon={<DSIconGlobe />}
              />
              {process.env.REACT_APP_MODE !== "open" && (
                <>
                  <MiniSortButton
                    tooltip="Order by both, project and personal tags"
                    buttonInfo={bag}
                    prop="tags"
                    icon={<Box bold>&</Box>}
                  />
                  <MiniSortButton
                    tooltip="Order by personal tags"
                    buttonInfo={bag}
                    prop="tagsPersonal"
                    icon={<DSIconUser />}
                  />
                </>
              )}
            </Group>
          </Col>
          <Col sp="8px">
            {showManualButton({arenaCtx, root}) && (
              <SortButton
                icon={<DSIconPyramid />}
                buttonInfo={bag}
                prop="manual"
                tooltipProps={{placement: "right"}}
                tooltip={
                  process.env.REACT_APP_MODE === "open"
                    ? "Order that has been manually set by the deck owners"
                    : "Allows you to set a order cards via drag and drop and organize them within Zones."
                }
              >
                Manual
              </SortButton>
            )}
            <SortButton buttonInfo={bag} prop="assignee">
              Owner
            </SortButton>
            <SortButton buttonInfo={bag} prop="lastEdit">
              Last updated
            </SortButton>
            <SortButton buttonInfo={bag} prop="creationDate">
              Creation date
            </SortButton>
            {arenaCtx.type !== "deck" && (
              <SortButton buttonInfo={bag} prop="deck">
                Deck
              </SortButton>
            )}
            {arenaCtx.type !== "deck" && (
              <SortButton buttonInfo={bag} prop="project">
                Project
              </SortButton>
            )}
            <SortButton buttonInfo={bag} prop="title">
              Title
            </SortButton>
            {process.env.REACT_APP_MODE !== "open" && arenaCtx.type === "deck" && (
              <SortButton
                buttonInfo={bag}
                prop="changes"
                tooltipProps={{placement: "right"}}
                tooltip="Shows you if cards are affected by notifications"
              >
                Changes
              </SortButton>
            )}
            {(current.prop === "upvotes" ||
              cards.some(
                (c) => (c.meta.discord && c.meta.discord.reactionCount) || c.meta.upvotes
              )) && (
              <SortButton buttonInfo={bag} prop="upvotes">
                Upvotes
              </SortButton>
            )}
            {process.env.REACT_APP_MODE !== "open" && root.account.timeTrackingMode !== "none" && (
              <SortButton buttonInfo={bag} prop="timeTracked">
                Time tracked
              </SortButton>
            )}
            {withDueDate && (
              <SortButton buttonInfo={bag} prop="dueDate">
                Due date
              </SortButton>
            )}
            <SortButton buttonInfo={bag} prop="heroCard">
              Hero Card
            </SortButton>
            {withBeasts && (
              <SortButton buttonInfo={bag} prop="beastLevel">
                Beast Level
              </SortButton>
            )}
          </Col>
          <Col sp="8px">
            <Row sp="4px">
              <Box flex="auto" bold color="primary" size={12}>
                Compact
              </Box>
              <ToggleButton
                onDark={!onLight}
                checked={current.isCompact}
                onChange={() => setCompact(!current.isCompact)}
                tooltip={
                  currOrderInfo?.multiKey || currOrderInfo?.noCompact
                    ? "This option cannot be used with the current ordering"
                    : "Toggle between grouping cards into several swim lanes or having a single swim lane only."
                }
                tooltipProps={{delayed: true, placement: "right"}}
                disabled={
                  !currOrderInfo || Boolean(currOrderInfo.multiKey) || currOrderInfo.noCompact
                }
              />
            </Row>
            <Row sp="4px">
              <Box flex="auto" bold color="primary" size={12}>
                Hide empty
              </Box>
              <ToggleButton
                tooltip={
                  current.prop !== "manual"
                    ? "This option is only available for the Manual order"
                    : null
                }
                onDark={!onLight}
                checked={current.hideEmpty}
                onChange={() => setHideEmpty(!current.hideEmpty)}
                disabled={current.prop !== "manual"}
              />
            </Row>
            <Row align="center" sp="4px">
              <Box flex="auto" bold color="primary" size={12}>
                Table
              </Box>
              <ToggleButton
                onDark={!onLight}
                checked={current.mode === "tableView"}
                onChange={() => setMode(current.mode === "tableView" ? "miniCard" : "tableView")}
              />
            </Row>
          </Col>
        </Col>
        {prefOrderInfo && (
          <Col
            sp="8px"
            px="12px"
            py="8px"
            rounded={4}
            borderWidth={1}
            borderColor="default"
            align="center"
          >
            <Box size={12} color="primary" bold textAlign="center">
              Default View
            </Box>
            <Row sp="8px">
              <DSButton
                variant="secondary"
                onClick={clear}
                size="sm"
                disabled={prefOrderInfo.isActive || !prefOrderInfo.preferredOrder}
              >
                Reset
              </DSButton>
              {canSetPrefOrder && prefOrderInfo.hasPermissionToModify && (
                <DefaultOrderButton
                  current={current}
                  disabled={prefOrderInfo.isActive}
                  updateFn={prefOrderInfo.updateFn}
                />
              )}
            </Row>
          </Col>
        )}
      </Col>
    </Col>
  );
};

export default SortPanel;
